import { WHALE_PORTRAIT_URL } from '../animalArtwork';
import { z } from 'zod';

const isoTimestampSchema = z.string().datetime({ offset: true });
export const calendarDateSchema = z.string().date();

export const createProfileRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(40).optional(),
});

export const recoverProfileRequestSchema = z.object({
  recoveryCode: z
    .string()
    .trim()
    .min(12)
    .max(40)
    .regex(/^[A-Za-z]+-[A-Za-z]+-[0-9]{4}$/),
});

export const legacyProfileDeletionRequestSchema = recoverProfileRequestSchema.extend({
  confirmation: z.literal('DELETE'),
});

export const advanceRequestSchema = z.object({
  calendarDate: z.string().date().optional(),
});

export const uuidSchema = z.string().uuid();

export const profileSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  createdAt: isoTimestampSchema,
});

export const profileSessionSchema = z.object({
  profile: profileSchema,
  recoveryCode: z.string().optional(),
});

export const animalSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  speciesName: z.string(),
  scientificName: z.string(),
  personality: z.string(),
  portraitUrl: z.string(),
  biography: z.string(),
  unlocked: z.boolean(),
});

export const trackPointSchema = z.object({
  id: uuidSchema,
  trackedAt: isoTimestampSchema,
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  distanceFromPreviousKm: z.number().nonnegative(),
  activityType: z.string(),
  activityMessage: z.string(),
});

export const animalTrackSchema = z.object({
  animal: animalSummarySchema.pick({
    id: true,
    name: true,
    speciesName: true,
    portraitUrl: true,
  }),
  points: z.array(trackPointSchema),
  lastUpdate: isoTimestampSchema,
  totalDistanceKm: z.number().nonnegative(),
  recentActivity: z.string(),
});

export const advanceResultSchema = z.object({
  created: z.boolean(),
  point: trackPointSchema,
  totalDistanceKm: z.number().nonnegative(),
});

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});

const accountEmailSchema = z.string().trim().toLowerCase().email().max(254);
const accountPasswordSchema = z.string().min(12).max(128);
export const distanceUnitSchema = z.enum(['miles', 'kilometers']);
const secureTokenSchema = z
  .string()
  .min(40)
  .max(100)
  .regex(/^[A-Za-z0-9_-]+$/);

export const registrationIntentRequestSchema = z.object({
  code: z.string().trim().min(1).max(64),
});

const policyVersionSchema = z.string().min(1).max(40);
const policyDigestSchema = z.string().regex(/^[a-f0-9]{64}$/);

export const registrationIntentResponseSchema = z
  .object({
    registrationToken: secureTokenSchema,
    expiresAt: isoTimestampSchema,
    policyVersion: policyVersionSchema.optional(),
    policyEffectiveAt: isoTimestampSchema.optional(),
    termsSha256: policyDigestSchema.optional(),
    privacySha256: policyDigestSchema.optional(),
  })
  .refine(
    (value) =>
      [value.policyVersion, value.policyEffectiveAt, value.termsSha256, value.privacySha256].every(
        Boolean,
      ) ||
      [value.policyVersion, value.policyEffectiveAt, value.termsSha256, value.privacySha256].every(
        (field) => field === undefined,
      ),
    { message: 'Registration policy identity must be supplied together.' },
  );

export const accountRegistrationRequestSchema = z
  .object({
    registrationToken: secureTokenSchema,
    firstName: z.string().trim().min(1).max(40),
    email: accountEmailSchema,
    password: accountPasswordSchema,
    adultConfirmed: z.literal(true),
    policyAccepted: z.literal(true).optional(),
    policyVersion: policyVersionSchema.optional(),
    termsSha256: policyDigestSchema.optional(),
    privacySha256: policyDigestSchema.optional(),
  })
  .refine(
    (value) =>
      [value.policyAccepted, value.policyVersion, value.termsSha256, value.privacySha256].every(
        Boolean,
      ) ||
      [value.policyAccepted, value.policyVersion, value.termsSha256, value.privacySha256].every(
        (field) => field === undefined,
      ),
    { message: 'Policy acceptance and identity must be supplied together.' },
  );

export const currentPolicySchema = z.object({
  version: policyVersionSchema,
  effectiveAt: isoTimestampSchema,
  termsSha256: policyDigestSchema,
  privacySha256: policyDigestSchema,
  accepted: z.boolean().optional(),
});

export const acceptPolicyRequestSchema = z.object({
  policyVersion: policyVersionSchema,
  termsSha256: policyDigestSchema,
  privacySha256: policyDigestSchema,
  accepted: z.literal(true),
});

export const privacyExportRequestSchema = z.object({
  password: z.string().min(1).max(128),
});

export const privacyRequestCreateSchema = z.object({
  userId: uuidSchema.nullable(),
  requestType: z.enum(['review', 'correct', 'copy', 'restrict', 'delete']),
  requesterReference: z.string().trim().min(1).max(120),
});

export const privacyRequestTransitionSchema = z.object({
  from: z.enum(['received', 'identity_verified', 'in_progress']),
  to: z.enum(['identity_verified', 'in_progress', 'completed', 'denied']),
  resolutionCode: z.string().trim().min(1).max(80).optional(),
});

export const retentionKindSchema = z.enum([
  'quiz_attempts',
  'sessions',
  'verification_tokens',
  'password_reset_tokens',
  'rate_limits',
  'unverified_accounts',
  'operational_messages',
  'privacy_request_metadata',
  'audit_metadata',
]);

export const boundedOperationResultSchema = z.object({
  processed: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

export const verifyEmailRequestSchema = z.object({ token: secureTokenSchema });
export const resendVerificationRequestSchema = z.object({ email: accountEmailSchema });
export const accountLoginRequestSchema = z.object({
  email: accountEmailSchema,
  password: z.string().min(1).max(128),
});
export const passwordResetRequestSchema = z.object({ email: accountEmailSchema });
export const passwordResetCompleteSchema = z.object({
  token: secureTokenSchema,
  password: accountPasswordSchema,
});
export const updateAccountRequestSchema = z
  .object({
    firstName: z.string().trim().min(1).max(40).optional(),
    distanceUnit: distanceUnitSchema.optional(),
  })
  .refine((value) => value.firstName !== undefined || value.distanceUnit !== undefined, {
    message: 'Choose an account detail to update.',
  });
export const changeAccountPasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: accountPasswordSchema,
  })
  .refine((value) => value.currentPassword !== value.newPassword, {
    message: 'Choose a new password that is different from the current password.',
    path: ['newPassword'],
  });
export const redeemAccountAnimalRequestSchema = z.object({
  code: z.string().trim().min(1).max(64),
  explorerId: uuidSchema,
});
export const accountExplorerBadgeSchema = z.enum([
  'sun',
  'moon',
  'star',
  'leaf',
  'wave',
  'mountain',
]);
export const accountExplorerSchema = z.object({
  id: uuidSchema,
  explorerNumber: z.number().int().positive(),
  label: z.string(),
  badge: accountExplorerBadgeSchema,
  animalCount: z.number().int().nonnegative(),
});
export const createAccountExplorerRequestSchema = z.object({
  nickname: z.string().trim().min(1).max(24),
  badge: accountExplorerBadgeSchema,
  idempotencyKey: uuidSchema,
});
export const updateAccountExplorerRequestSchema = z.object({
  nickname: z.string().trim().min(1).max(24),
  badge: accountExplorerBadgeSchema,
});
export const deleteAccountExplorerRequestSchema = z.object({
  confirmation: z.literal('DELETE'),
});
export const accountExplorerCreationPreviewSchema = z.object({
  eligibleAnimals: z.array(
    z.object({ simulatedAnimalId: uuidSchema, name: z.string(), portraitUrl: z.string() }),
  ),
});

export const genericAcceptedSchema = z.object({ accepted: z.literal(true) });
export const accountDeletionProgressSchema = z.object({
  state: z.enum(['in_progress', 'completed']),
  phase: z.string().min(1).max(80),
  processed: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export const accountRegistrationResponseSchema = z.object({
  accepted: z.literal(true),
  verificationRequired: z.literal(true),
  verificationEmailQueued: z.boolean().optional(),
  developmentVerificationToken: secureTokenSchema.optional(),
});
export const passwordResetResponseSchema = z.object({
  accepted: z.literal(true),
  developmentResetToken: secureTokenSchema.optional(),
});
export const resendVerificationResponseSchema = z.object({
  accepted: z.literal(true),
  developmentVerificationToken: secureTokenSchema.optional(),
});
export const accountSchema = z.object({
  id: uuidSchema,
  firstName: z.string(),
  email: accountEmailSchema,
  emailVerifiedAt: isoTimestampSchema,
  distanceUnit: distanceUnitSchema,
  policyAccepted: z.boolean(),
});
export const accountAnimalSchema = z.object({
  speciesId: uuidSchema,
  productId: uuidSchema,
  productSlug: z.string(),
  productName: z.string(),
  productDescription: z.string(),
  previewImageUrl: z.string(),
  unlocked: z.boolean(),
  explorerId: uuidSchema.optional(),
  explorerNumber: z.number().int().positive().optional(),
  explorerLabel: z.string().optional(),
  explorerBadge: accountExplorerBadgeSchema.optional(),
  entitlementId: uuidSchema.optional(),
  simulatedAnimalId: uuidSchema.optional(),
  animalName: z.string().optional(),
  personality: z.string().optional(),
  biography: z.string().optional(),
  portraitUrl: z.string().optional(),
});
export const accountAnimalDetailSchema = z.object({
  entitlementId: uuidSchema,
  simulatedAnimalId: uuidSchema,
  speciesId: uuidSchema,
  name: z.string(),
  personality: z.string(),
  biography: z.string(),
  portraitUrl: z.string(),
  homeRegion: z.string(),
  habitat: z.string(),
});
export const accountHistoryQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(30).default(15),
});
export const localDailyJobRetrySchema = z.object({ localDate: calendarDateSchema });
export const dailyGenerationResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('completed'),
    runId: uuidSchema,
    localDate: calendarDateSchema,
    inserted: z.number().int().nonnegative(),
    existing: z.number().int().nonnegative(),
  }),
  z.object({
    status: z.literal('already_completed'),
    runId: uuidSchema,
    localDate: calendarDateSchema,
  }),
  z.object({
    status: z.literal('busy'),
    runId: uuidSchema,
    localDate: calendarDateSchema,
  }),
  z.object({
    status: z.literal('failed'),
    runId: uuidSchema,
    localDate: calendarDateSchema,
    errorCode: z.string(),
  }),
]);
export const accountAnimalHistoryDaySchema = z.object({
  date: z.string().date(),
  status: z.string(),
  dailyDistanceKm: z.number().nonnegative(),
  headline: z.string(),
  shortNarrative: z.string(),
  points: z.array(trackPointSchema).min(1),
});
const accountAnimalTrackIdentitySchema = z.object({
  animal: z.object({
    id: uuidSchema,
    name: z.string(),
    speciesName: z.string(),
    portraitUrl: z.string(),
  }),
});
export const accountAnimalTrackSchema = z.discriminatedUnion('status', [
  accountAnimalTrackIdentitySchema.extend({ status: z.literal('preparing') }),
  accountAnimalTrackIdentitySchema.extend({
    status: z.literal('ready'),
    days: z.array(accountAnimalHistoryDaySchema).min(1),
    points: z.array(trackPointSchema).min(1),
    lastUpdate: isoTimestampSchema,
    totalDistanceKm: z.number().nonnegative(),
    recentActivity: z.string(),
  }),
]);
export const accountAnimalActivitySchema = z.object({
  animalId: uuidSchema,
  activities: z.array(
    z.object({
      date: z.string().date(),
      status: z.string(),
      headline: z.string(),
      activityMessage: z.string(),
      dailyDistanceKm: z.number().nonnegative(),
    }),
  ),
});
export const accountAnimalDailySchema = z.object({
  animalId: uuidSchema,
  day: accountAnimalHistoryDaySchema,
});
export const learningDifficultySchema = z.enum(['early', 'growing', 'challenge']);
export const learningQuestionFormatSchema = z.enum([
  'multiple_choice',
  'true_false',
  'match_fact',
  'identify_habitat',
  'journey',
  'behavior',
  'vocabulary',
]);
export const accountAnimalFactSchema = z.object({
  animalId: uuidSchema,
  date: calendarDateSchema,
  fact: z.object({
    id: uuidSchema,
    text: z.string(),
    explanation: z.string(),
    difficulty: learningDifficultySchema,
  }),
});
export const accountAnimalStorySchema = z.object({
  animalId: uuidSchema,
  date: calendarDateSchema,
  story: z.object({
    id: uuidSchema,
    title: z.string(),
    narrative: z.string(),
    educationalExplanation: z.string(),
  }),
});
export const learningChoiceSchema = z.object({ id: z.string().min(1), label: z.string().min(1) });
export const accountAnimalQuizSchema = z.object({
  assignmentId: uuidSchema,
  animalId: uuidSchema,
  date: calendarDateSchema,
  questions: z.array(
    z.object({
      id: uuidSchema,
      format: learningQuestionFormatSchema,
      difficulty: learningDifficultySchema,
      prompt: z.string(),
      choices: z.array(learningChoiceSchema).min(2),
    }),
  ),
});
export const submitQuizAttemptSchema = z.object({
  answers: z
    .array(
      z.object({
        questionId: uuidSchema,
        choiceId: z.string().min(1).max(80),
      }),
    )
    .min(1)
    .max(20),
});
export const quizAttemptResultSchema = z.object({
  attemptId: uuidSchema,
  assignmentId: uuidSchema,
  correctCount: z.number().int().nonnegative(),
  totalCount: z.number().int().positive(),
  results: z.array(
    z.object({
      questionId: uuidSchema,
      selectedChoiceId: z.string(),
      correctChoiceId: z.string(),
      correct: z.boolean(),
      explanation: z.string(),
    }),
  ),
});
export const quizAttemptHistorySchema = z.object({
  animalId: uuidSchema,
  date: calendarDateSchema,
  attempts: z.array(
    z.object({
      attemptId: uuidSchema,
      assignmentId: uuidSchema,
      correctCount: z.number().int().nonnegative(),
      totalCount: z.number().int().positive(),
      completedAt: isoTimestampSchema,
    }),
  ),
});
export const accountAnimalLearningActivitySchema = z.object({
  id: uuidSchema,
  activityType: z.string(),
  title: z.string(),
  instructions: z.string(),
  config: z.record(z.string(), z.unknown()),
});
export const accountAnimalActivitiesSchema = z.object({
  animalId: uuidSchema,
  date: calendarDateSchema,
  activities: z.array(accountAnimalLearningActivitySchema),
});
export const accountAnimalTodayReadySchema = z.object({
  status: z.literal('ready'),
  animalId: uuidSchema,
  date: calendarDateSchema,
  fact: accountAnimalFactSchema.shape.fact,
  story: accountAnimalStorySchema.shape.story,
  quiz: accountAnimalQuizSchema,
  activities: z.array(accountAnimalLearningActivitySchema),
});
export const accountAnimalTodayPreparingSchema = z.object({
  status: z.literal('preparing'),
  animalId: uuidSchema,
  date: calendarDateSchema,
});
export const accountAnimalTodaySchema = z.discriminatedUnion('status', [
  accountAnimalTodayReadySchema,
  accountAnimalTodayPreparingSchema,
]);
export const contentTypeSchema = z.enum([
  'source',
  'fact',
  'question',
  'activity_template',
  'story_template',
  'activity',
]);
export const contentListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const contentRecordSchema = z.object({
  id: uuidSchema,
  contentType: contentTypeSchema,
  active: z.boolean(),
  data: z.record(z.string(), z.unknown()),
  updatedAt: isoTimestampSchema,
});
export const contentListResponseSchema = z.object({
  items: z.array(contentRecordSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});
export const contentWriteRequestSchema = z.object({
  data: z.record(z.string(), z.unknown()),
});
export const redeemAccountAnimalResponseSchema = z.object({
  status: z.enum(['unlocked', 'already_unlocked']),
  entitlementId: uuidSchema,
  simulatedAnimalId: uuidSchema,
});

export const inventoryStatusSchema = z.enum([
  'in_stock',
  'low_stock',
  'out_of_stock',
  'coming_soon',
]);
export const storefrontProductSchema = z.object({
  id: uuidSchema,
  speciesId: uuidSchema,
  slug: z.string().min(3).max(80),
  name: z.string().min(1).max(100),
  description: z.string(),
  featured: z.boolean(),
  species: z.object({
    commonName: z.string(),
    scientificName: z.string(),
    habitat: z.string(),
  }),
  images: z.array(
    z.object({
      url: z.union([z.string().startsWith('/api/v1/assets/'), z.literal(WHALE_PORTRAIT_URL)]),
      altText: z.string().min(1).max(180),
    }),
  ),
  experience: z.object({
    simulatedTracking: z.literal(true),
    dailyLearning: z.literal(true),
    standardInstructionCard: z.literal(true),
  }),
});
export const storefrontCatalogSchema = z.object({
  products: z.array(storefrontProductSchema),
});
export const storefrontProductSlugSchema = z
  .string()
  .min(3)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

export const checkoutItemSchema = z.object({
  productId: uuidSchema,
  quantity: z.number().int().min(1).max(5),
});
export const createCheckoutSessionSchema = z.object({
  contactEmail: accountEmailSchema,
  items: z.array(checkoutItemSchema).min(1).max(10),
});
export const checkoutIdempotencyKeySchema = z
  .string()
  .min(16)
  .max(100)
  .regex(/^[A-Za-z0-9_-]+$/);
export const orderStatusSchema = z.enum([
  'pending',
  'paid',
  'payment_failed',
  'canceled',
  'fulfilled',
  'refunded',
]);
export const orderItemSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema.nullable(),
  sku: z.string(),
  name: z.string(),
  unitPriceCents: z.number().int().nonnegative(),
  quantity: z.number().int().positive(),
  lineTotalCents: z.number().int().nonnegative(),
  instructionCard: z.object({
    speciesName: z.string(),
    label: z.string(),
  }),
});
export const orderSummarySchema = z.object({
  id: uuidSchema,
  orderNumber: z.string(),
  status: orderStatusSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  subtotalCents: z.number().int().nonnegative(),
  shippingCents: z.number().int().nonnegative(),
  taxCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  createdAt: isoTimestampSchema,
  completedAt: isoTimestampSchema.nullable(),
  canCancel: z.boolean(),
  items: z.array(orderItemSchema),
});
export const checkoutSessionSchema = z.object({
  order: orderSummarySchema,
  checkoutUrl: z.string().url(),
  expiresAt: isoTimestampSchema,
  confirmationToken: secureTokenSchema.optional(),
});
export const accountOrderListSchema = z.object({ orders: z.array(orderSummarySchema) });
export const paymentWebhookResponseSchema = z.object({
  accepted: z.literal(true),
  status: z.enum(['processed', 'duplicate', 'ignored', 'rejected']),
});

export const adminRoleSchema = z.enum(['administrator', 'content_editor', 'fulfillment']);
export const adminSessionSchema = z.object({
  roles: z.array(adminRoleSchema),
  freshUntil: isoTimestampSchema.nullable(),
});
export const adminReauthenticateRequestSchema = z.object({
  password: z.string().min(1).max(128),
});
export const adminConfirmationSchema = z.object({
  confirmation: z.literal('CONFIRM'),
  reason: z.string().trim().min(3).max(240),
});
export const adminListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(25),
  search: z.string().trim().max(254).optional(),
  status: z.string().trim().max(40).optional(),
});
export const adminUserListQuerySchema = adminListQuerySchema.extend({
  status: z.enum(['pending', 'active', 'disabled', 'deletion_requested']).optional(),
});
export const adminOrderListQuerySchema = adminListQuerySchema.extend({
  status: orderStatusSchema.optional(),
});
export const adminJobListQuerySchema = adminListQuerySchema.extend({
  status: z.enum(['running', 'completed', 'failed']).optional(),
});
export const adminPaginatedSchema = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  });
export const adminOverviewSchema = z.object({
  users: z.number().int().nonnegative(),
  activeEntitlements: z.number().int().nonnegative(),
  activeCodes: z.number().int().nonnegative(),
  failedJobs: z.number().int().nonnegative(),
  openDeletionRequests: z.number().int().nonnegative(),
  successfulCodeRedemptions: z.number().int().nonnegative(),
});
export const adminAccessCodeSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  productName: z.string(),
  speciesName: z.string(),
  animalName: z.string(),
  maskedIdentifier: z.string(),
  codeVersion: z.number().int().positive(),
  active: z.boolean(),
  expiresAt: isoTimestampSchema.nullable(),
  usageCount: z.number().int().nonnegative(),
  activeEntitlementCount: z.number().int().nonnegative(),
  recentResult: z.string().nullable(),
  updatedAt: isoTimestampSchema,
});
export const operatorProvisionAccessCodeSchema = z
  .object({
    targetEnvironment: z.literal('production'),
    installationMode: z.enum(['active', 'whale_launch_staged']).default('active'),
    confirmation: z.enum(['INSTALL STATIC ANIMAL CODE', 'STAGE WHALE LAUNCH CODE']),
    productId: uuidSchema,
    simulatedAnimalId: uuidSchema,
    code: z.string().trim().min(8).max(32),
    administrativeLabel: z.string().trim().min(1).max(80),
    operatorId: z
      .string()
      .trim()
      .regex(/^[a-z0-9][a-z0-9._-]{2,63}$/),
  })
  .superRefine((value, context) => {
    const expected =
      value.installationMode === 'active'
        ? 'INSTALL STATIC ANIMAL CODE'
        : 'STAGE WHALE LAUNCH CODE';
    if (value.confirmation !== expected) {
      context.addIssue({
        code: 'custom',
        path: ['confirmation'],
        message: 'The confirmation does not match the requested installation mode.',
      });
    }
  });
export const operatorInstalledAccessCodeSchema = z.object({
  status: z.enum(['created', 'staged_existing']),
  id: uuidSchema,
  productId: uuidSchema,
  simulatedAnimalId: uuidSchema,
  maskedIdentifier: z.string(),
  codeVersion: z.number().int().positive(),
});
export const operatorVerifyAccessCodeSchema = z.object({
  targetEnvironment: z.literal('production'),
  confirmation: z.literal('VERIFY PRODUCTION ACCESS CODE'),
  code: z.string().trim().min(1).max(64),
});
export const operatorAccessCodeVerificationSchema = z.object({
  present: z.literal(true),
  productId: uuidSchema,
  simulatedAnimalId: uuidSchema,
  maskedIdentifier: z.string(),
  active: z.boolean(),
  expiresAt: isoTimestampSchema.nullable(),
  codeVersion: z.number().int().positive(),
});
export const adminCodeStateRequestSchema = adminConfirmationSchema.extend({
  active: z.boolean(),
  expiresAt: isoTimestampSchema.nullable(),
});
export const adminEntitlementSchema = z.object({
  id: uuidSchema,
  speciesName: z.string(),
  animalName: z.string(),
  explorerNumber: z.number().int().positive(),
  status: z.enum(['active', 'revoked']),
  unlockedAt: isoTimestampSchema,
  revokedAt: isoTimestampSchema.nullable(),
});
export const adminUserSchema = z.object({
  id: uuidSchema,
  firstName: z.string(),
  email: accountEmailSchema,
  status: z.enum(['pending', 'active', 'disabled', 'deletion_requested']),
  createdAt: isoTimestampSchema,
  activeSessionCount: z.number().int().nonnegative(),
  roles: z.array(adminRoleSchema),
  entitlements: z.array(adminEntitlementSchema),
});
export const adminOrderSchema = z.object({
  id: uuidSchema,
  orderNumber: z.string(),
  status: orderStatusSchema,
  contactEmail: accountEmailSchema.nullable(),
  totalCents: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  createdAt: isoTimestampSchema,
  fulfillmentStatus: z.enum(['pending', 'packing', 'shipped', 'delivered', 'canceled']).nullable(),
  carrier: z.string().nullable(),
  trackingReference: z.string().nullable(),
  instructionCards: z.array(z.string()),
});
export const adminOrderFinancialsSchema = z.object({
  orderId: uuidSchema,
  orderStatus: orderStatusSchema,
  payment: z
    .object({
      id: uuidSchema,
      status: z.enum([
        'pending',
        'succeeded',
        'failed',
        'canceled',
        'partially_refunded',
        'refunded',
      ]),
      amountCents: z.number().int().nonnegative(),
      currency: z.string().regex(/^[A-Z]{3}$/),
      refundableCents: z.number().int().nonnegative(),
    })
    .nullable(),
  refunds: z.array(
    z.object({
      id: uuidSchema,
      status: z.string(),
      amountCents: z.number().int().positive(),
      reason: z.string().nullable(),
    }),
  ),
  reconciliation: z.enum(['matched', 'payment_missing', 'amount_mismatch']),
});
export const adminFulfillmentTransitionSchema = adminConfirmationSchema.extend({
  status: z.enum(['packing', 'shipped', 'delivered', 'canceled']),
  carrier: z.string().trim().max(80).optional(),
  trackingReference: z.string().trim().max(160).optional(),
});
export const adminRefundRequestSchema = adminConfirmationSchema.extend({
  paymentId: uuidSchema,
  amountCents: z.number().int().positive().max(1_000_000),
  idempotencyKey: checkoutIdempotencyKeySchema,
});
export const adminJobRunSchema = z.object({
  id: uuidSchema,
  localDate: calendarDateSchema,
  attempt: z.number().int().positive(),
  status: z.enum(['running', 'completed', 'failed']),
  generatedRecordCount: z.number().int().nonnegative(),
  requiredAnimalCount: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  startedAt: isoTimestampSchema,
  completedAt: isoTimestampSchema.nullable(),
});
export const adminAuditLogSchema = z.object({
  id: uuidSchema,
  actorUserId: uuidSchema.nullable(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  outcome: z.enum(['succeeded', 'denied', 'failed']),
  reason: z.string().nullable(),
  createdAt: isoTimestampSchema,
});
export const adminCatalogSchema = z.object({
  species: z.array(
    z.object({
      id: uuidSchema,
      commonName: z.string(),
      scientificName: z.string(),
      habitatType: z.enum(['marine', 'coastal', 'terrestrial']),
      defaultRegion: z.string(),
      movementConfig: z.record(z.string(), z.unknown()),
    }),
  ),
  products: z.array(
    z.object({
      id: uuidSchema,
      name: z.string(),
      description: z.string(),
      active: z.boolean(),
      featured: z.boolean(),
      speciesName: z.string(),
      images: z.array(
        z.object({
          id: uuidSchema,
          assetKey: z.string(),
          altText: z.string(),
          sortOrder: z.number().int().nonnegative(),
        }),
      ),
    }),
  ),
  animals: z.array(
    z.object({
      id: uuidSchema,
      speciesName: z.string(),
      name: z.string(),
      assignmentSlot: z.number().int().min(0).max(4),
      status: z.string(),
    }),
  ),
});
export const adminProductUpdateSchema = adminConfirmationSchema.extend({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().min(1).max(2_000),
  active: z.boolean(),
  featured: z.boolean(),
});
export const adminProductImageUpdateSchema = adminConfirmationSchema.extend({
  altText: z.string().trim().min(1).max(180),
  sortOrder: z.number().int().min(0).max(1_000),
});
export const adminSpeciesUpdateSchema = adminConfirmationSchema.extend({
  commonName: z.string().trim().min(1).max(100),
  scientificName: z.string().trim().min(1).max(120),
  habitatType: z.enum(['marine', 'coastal', 'terrestrial']),
  defaultRegion: z.string().trim().min(1).max(160),
  movementConfig: z.record(z.string(), z.unknown()),
});
export const adminAnimalStatusUpdateSchema = adminConfirmationSchema.extend({
  status: z.enum(['active', 'resting', 'migrating', 'nesting', 'feeding', 'exploring', 'inactive']),
});
export const adminRoleChangeSchema = adminConfirmationSchema.extend({ role: adminRoleSchema });
export const adminJobRetryRequestSchema = adminConfirmationSchema.extend({
  localDate: calendarDateSchema,
});
export const accountDeletionRequestSchema = z.object({
  password: z.string().min(1).max(128),
  confirmation: z.literal('DELETE'),
});
